import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Megaphone, Pause, Play, Plus, Send, Square, Trash2, Users } from "lucide-react";
import { api, apiError } from "@/lib/api";

const inputCls = "h-10 w-full rounded-xl border border-white/10 bg-black/40 px-3 text-xs text-white placeholder:text-neutral-600 focus:border-amber-500/60 focus:outline-none";
const labelCls = "mb-1.5 block text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500";

const STATUS_STYLE = {
  draft: "border-white/15 text-neutral-400",
  scheduled: "border-sky-500/40 text-sky-400",
  active: "border-emerald-500/40 text-emerald-400",
  paused: "border-amber-500/40 text-amber-400",
  ended: "border-white/10 text-neutral-600",
};

const EMPTY_FORM = {
  name: "", description: "", title: "", message: "",
  start_date: "", end_date: "", publish: false,
};

function ruleLabel(rule, presets) {
  const meta = (presets || []).find((p) => p.type === rule.type);
  if (!meta) return rule.type;
  const bits = [];
  if (rule.days) bits.push(`${rule.days}d`);
  if (rule.min_spend) bits.push(`≥ ₹${Number(rule.min_spend).toLocaleString("en-IN")}`);
  if (rule.product_id) bits.push("product");
  if (rule.category) bits.push(rule.category);
  return `${meta.label}${bits.length ? ` (${bits.join(", ")})` : ""}`;
}

export function SegmentBuilder({ rules, setRules, presets, products, categories }) {
  const [count, setCount] = useState(null);
  const [counting, setCounting] = useState(false);
  const timer = useRef(null);

  const activeTypes = useMemo(() => new Set(rules.map((r) => r.type)), [rules]);

  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setCounting(true);
      try {
        const { data } = await api.post("/admin/segments/count", { rules });
        setCount(data.count);
      } catch {
        setCount(null);
      } finally {
        setCounting(false);
      }
    }, 350);
    return () => clearTimeout(timer.current);
  }, [rules]);

  const toggle = (type, params) => {
    if (activeTypes.has(type)) {
      setRules(rules.filter((r) => r.type !== type));
    } else {
      setRules([...rules, { type, ...params }]);
    }
  };

  const setParam = (type, key, value) =>
    setRules(rules.map((r) => (r.type === type ? { ...r, [key]: value } : r)));

  return (
    <div data-testid="segment-builder">
      <div className="grid gap-2 sm:grid-cols-2">
        {(presets || []).map((p) => {
          const on = activeTypes.has(p.type);
          const rule = rules.find((r) => r.type === p.type);
          return (
            <div key={p.type} className={`rounded-xl border p-3 transition-colors ${on ? "border-amber-500/50 bg-amber-500/[0.06]" : "border-white/[0.07] bg-white/[0.02]"}`}>
              <label className="flex cursor-pointer items-start gap-2.5" data-testid={`segment-rule-${p.type}`}>
                <input type="checkbox" checked={on} onChange={() => toggle(p.type, p.params)}
                  className="mt-0.5 h-4 w-4 accent-amber-500" />
                <span className="min-w-0 flex-1">
                  <span className="block text-xs font-bold text-white">{p.label}</span>
                  <span className="block text-[10px] text-neutral-500">{p.hint} · {p.count} now</span>
                </span>
              </label>
              {on && rule && "days" in rule && (
                <div className="mt-2 flex items-center gap-2 pl-6">
                  <input type="number" min="1" max="365" value={rule.days}
                    onChange={(e) => setParam(p.type, "days", parseInt(e.target.value) || 1)}
                    data-testid={`segment-param-days-${p.type}`}
                    className="h-8 w-20 rounded-lg border border-white/10 bg-black/40 px-2 text-xs text-white focus:border-amber-500/60 focus:outline-none" />
                  <span className="text-[10px] text-neutral-500">days</span>
                </div>
              )}
              {on && rule && "min_spend" in rule && (
                <div className="mt-2 flex items-center gap-2 pl-6">
                  <span className="text-[10px] text-neutral-500">₹</span>
                  <input type="number" min="0" value={rule.min_spend}
                    onChange={(e) => setParam(p.type, "min_spend", parseFloat(e.target.value) || 0)}
                    data-testid="segment-param-min-spend"
                    className="h-8 w-28 rounded-lg border border-white/10 bg-black/40 px-2 text-xs text-white focus:border-amber-500/60 focus:outline-none" />
                  <span className="text-[10px] text-neutral-500">min lifetime spend</span>
                </div>
              )}
              {on && rule && "product_id" in rule && (
                <select value={rule.product_id} onChange={(e) => setParam(p.type, "product_id", e.target.value)}
                  data-testid={`segment-param-product-${p.type}`}
                  className="ml-6 mt-2 h-8 w-[calc(100%-1.5rem)] rounded-lg border border-white/10 bg-black/40 px-2 text-xs text-white focus:border-amber-500/60 focus:outline-none">
                  <option value="">Select product…</option>
                  {products.map((pr) => <option key={pr.id} value={pr.id}>{pr.name}</option>)}
                </select>
              )}
              {on && rule && "category" in rule && (
                <select value={rule.category} onChange={(e) => setParam(p.type, "category", e.target.value)}
                  data-testid={`segment-param-category-${p.type}`}
                  className="ml-6 mt-2 h-8 w-[calc(100%-1.5rem)] rounded-lg border border-white/10 bg-black/40 px-2 text-xs text-white focus:border-amber-500/60 focus:outline-none">
                  <option value="">Select category…</option>
                  {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-3 flex items-center gap-2 rounded-xl border border-amber-500/25 bg-amber-500/[0.05] px-4 py-3" data-testid="segment-live-count">
        {counting ? <Loader2 className="h-4 w-4 animate-spin text-amber-500" /> : <Users className="h-4 w-4 text-amber-500" />}
        <p className="text-xs font-bold text-amber-300">
          {count === null ? "…" : `${count} customer${count === 1 ? "" : "s"} match ${rules.length === 0 ? "(all customers)" : "this segment"}`}
        </p>
      </div>
    </div>
  );
}

export default function AdminCampaigns() {
  const navigate = useNavigate();
  const [presets, setPresets] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [campaigns, setCampaigns] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [rules, setRules] = useState([]);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);

  const load = useCallback(() => {
    const params = statusFilter === "all" ? {} : { status: statusFilter };
    api.get("/admin/campaigns", { params }).then(({ data }) => setCampaigns(data.campaigns))
      .catch((e) => toast.error(apiError(e, "Couldn't load campaigns")));
  }, [statusFilter]);
  useEffect(load, [load]);

  useEffect(() => {
    api.get("/admin/segments/presets").then(({ data }) => setPresets(data.presets)).catch(() => {});
    api.get("/admin/products").then(({ data }) => {
      const list = Array.isArray(data) ? data : data.products || [];
      setProducts(list.map((p) => ({ id: p.id, name: p.name })));
    }).catch(() => {});
    api.get("/admin/categories").then(({ data }) => {
      const names = [];
      (data.categories || []).forEach((c) => {
        names.push(c.name);
        (c.subcategories || []).forEach((s) => names.push(s.name));
      });
      setCategories(names);
    }).catch(() => {});
    api.get("/admin/campaigns/settings").then(({ data }) => setSettings(data)).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const create = async (publish) => {
    setSaving(true);
    try {
      const { data } = await api.post("/admin/campaigns", { ...form, segment: { rules }, publish });
      toast.success(publish
        ? `Campaign "${data.campaign.name}" is ${data.campaign.status}`
        : `Draft "${data.campaign.name}" saved — ${data.campaign.audience_size} customer(s) in audience`);
      setShowCreate(false);
      setForm(EMPTY_FORM);
      setRules([]);
      load();
    } catch (e) {
      toast.error(apiError(e, "Couldn't save campaign"));
    } finally {
      setSaving(false);
    }
  };

  const action = async (c, verb) => {
    if (verb === "delete" && !window.confirm(`Delete draft "${c.name}"? This cannot be undone.`)) return;
    if (verb === "end" && !window.confirm(`End "${c.name}" now? Customers will stop seeing it.`)) return;
    try {
      if (verb === "delete") await api.delete(`/admin/campaigns/${c.id}`);
      else await api.post(`/admin/campaigns/${c.id}/${verb}`);
      toast.success({ publish: "Published", pause: "Paused", resume: "Resumed", end: "Ended", delete: "Deleted" }[verb]);
      load();
    } catch (e) {
      toast.error(apiError(e, `Couldn't ${verb} campaign`));
    }
  };

  const toggleEmail = async () => {
    try {
      const { data } = await api.put("/admin/campaigns/settings", { email_enabled: !settings.email_enabled });
      setSettings(data);
      toast.success(data.email_enabled ? "Campaign emails ON — one per customer per campaign" : "Campaign emails OFF");
    } catch (e) {
      toast.error(apiError(e, "Couldn't update settings"));
    }
  };

  return (
    <div data-testid="admin-campaigns" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-white">
            <Megaphone className="h-6 w-6 text-amber-400" /> Campaigns
          </h1>
          <p className="mt-1 text-sm text-neutral-500">
            Target customer segments with in-app promotions. Counts are always live from real customer data — no discounts or prices are touched.
          </p>
        </div>
        <button onClick={() => setShowCreate((s) => !s)} data-testid="campaign-create-toggle"
          className="flex items-center gap-2 rounded-xl bg-amber-500 px-5 py-3 text-sm font-bold text-black transition-colors hover:bg-amber-400">
          <Plus className="h-4 w-4" /> New Campaign
        </button>
      </div>

      {settings && (
        <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-white/[0.07] bg-[#FFFEFA] px-4 py-3" data-testid="campaign-email-gate">
          <Send className="h-4 w-4 text-neutral-500" />
          <p className="text-xs text-neutral-400">
            Email delivery <span className={`font-bold ${settings.email_enabled ? "text-emerald-400" : "text-neutral-300"}`}>{settings.email_enabled ? "ON" : "OFF"}</span>
            <span className="text-neutral-600"> — in-app banners always work; email sends at most one per customer per campaign.</span>
          </p>
          <button onClick={toggleEmail} role="switch" aria-checked={settings.email_enabled} data-testid="campaign-email-toggle"
            className={`relative ml-auto h-6 w-11 shrink-0 rounded-full transition-colors ${settings.email_enabled ? "bg-amber-500" : "bg-white/10"}`}>
            <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${settings.email_enabled ? "left-5" : "left-0.5"}`} />
          </button>
        </div>
      )}

      {showCreate && (
        <div className="rounded-2xl border border-amber-500/25 bg-[#FFFEFA] p-5 sm:p-6" data-testid="campaign-create-form">
          <h2 className="text-sm font-bold uppercase tracking-wider text-neutral-400">New campaign</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Campaign name (internal)</label>
              <input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="campaign-form-name" className={inputCls} placeholder="e.g. Win back inactive buyers" />
            </div>
            <div>
              <label className={labelCls}>Internal description</label>
              <input value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="campaign-form-description" className={inputCls} placeholder="Notes for the team — customers never see this" />
            </div>
            <div>
              <label className={labelCls}>Customer-facing title</label>
              <input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="campaign-form-title" className={inputCls} placeholder="e.g. New foil rolls are in stock" />
            </div>
            <div>
              <label className={labelCls}>Customer-facing message</label>
              <input value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="campaign-form-message" className={inputCls} placeholder="Short, honest promo text shown in the shop" />
            </div>
            <div>
              <label className={labelCls}>Start</label>
              <input type="datetime-local" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} data-testid="campaign-form-start" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>End</label>
              <input type="datetime-local" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} data-testid="campaign-form-end" className={inputCls} />
            </div>
          </div>
          <div className="mt-5">
            <label className={labelCls}>Target segment — combine filters (AND)</label>
            <SegmentBuilder rules={rules} setRules={setRules} presets={presets} products={products} categories={categories} />
          </div>
          <div className="mt-5 flex flex-wrap gap-3">
            <button onClick={() => create(false)} disabled={saving || form.name.trim().length < 2 || form.title.trim().length < 2 || form.message.trim().length < 2 || !form.start_date || !form.end_date}
              data-testid="campaign-save-draft"
              className="flex items-center gap-2 rounded-xl border border-white/15 px-5 py-2.5 text-xs font-bold text-neutral-200 transition-colors hover:border-amber-500/50 disabled:opacity-50">
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Save as Draft
            </button>
            <button onClick={() => create(true)} disabled={saving || form.name.trim().length < 2 || form.title.trim().length < 2 || form.message.trim().length < 2 || !form.start_date || !form.end_date}
              data-testid="campaign-save-publish"
              className="flex items-center gap-2 rounded-xl bg-amber-500 px-5 py-2.5 text-xs font-bold text-black transition-colors hover:bg-amber-400 disabled:opacity-50">
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Publish / Schedule
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2" data-testid="campaign-status-filters">
        {["all", "draft", "scheduled", "active", "paused", "ended"].map((s) => (
          <button key={s} onClick={() => setStatusFilter(s)} data-testid={`campaign-filter-${s}`}
            className={`rounded-full border px-4 py-2 text-xs font-bold capitalize transition-colors ${
              statusFilter === s ? "border-amber-500 bg-amber-500/10 text-amber-400" : "border-white/10 text-neutral-400 hover:border-white/25"}`}>
            {s}
          </button>
        ))}
      </div>

      <div className="overflow-hidden rounded-2xl border border-white/[0.07] bg-[#FFFEFA]" data-testid="campaign-list">
        {!campaigns ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-neutral-600" /></div>
        ) : campaigns.length === 0 ? (
          <p className="py-16 text-center text-sm text-neutral-500" data-testid="campaign-empty">No campaigns yet — create your first one above.</p>
        ) : (
          campaigns.map((c) => (
            <div key={c.id} className="flex flex-wrap items-center gap-3 border-b border-white/[0.05] px-4 py-3.5 last:border-0" data-testid={`campaign-row-${c.id}`}>
              <button onClick={() => navigate(`/admin/campaigns/${c.id}`)} data-testid={`campaign-open-${c.id}`} className="min-w-0 flex-1 text-left">
                <span className="block truncate text-sm font-bold text-white hover:text-amber-400">{c.name}</span>
                <span className="block truncate text-[11px] text-neutral-500">
                  {c.title} · {new Date(c.start_date).toLocaleDateString("en-IN", { day: "numeric", month: "short" })} → {new Date(c.end_date).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                </span>
              </button>
              <span className="text-xs font-bold text-white" data-testid={`campaign-audience-${c.id}`}>{c.audience_size} <span className="font-medium text-neutral-500">customers</span></span>
              <span className="text-[11px] text-neutral-500" data-testid={`campaign-stats-${c.id}`}>{c.stats.impressions} views · {c.stats.dismissals} dismissed</span>
              <span className={`rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${STATUS_STYLE[c.status]}`}
                data-testid={`campaign-status-${c.id}`}>{c.status}</span>
              <span className="flex items-center gap-1.5">
                {c.status === "draft" && (
                  <button onClick={() => action(c, "publish")} data-testid={`campaign-publish-${c.id}`} title="Publish"
                    className="flex h-8 w-8 items-center justify-center rounded-lg border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10"><Play className="h-3.5 w-3.5" /></button>
                )}
                {c.status === "active" && (
                  <button onClick={() => action(c, "pause")} data-testid={`campaign-pause-${c.id}`} title="Pause"
                    className="flex h-8 w-8 items-center justify-center rounded-lg border border-amber-500/40 text-amber-400 hover:bg-amber-500/10"><Pause className="h-3.5 w-3.5" /></button>
                )}
                {c.status === "paused" && (
                  <button onClick={() => action(c, "resume")} data-testid={`campaign-resume-${c.id}`} title="Resume"
                    className="flex h-8 w-8 items-center justify-center rounded-lg border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10"><Play className="h-3.5 w-3.5" /></button>
                )}
                {!["ended"].includes(c.status) && c.status !== "draft" && (
                  <button onClick={() => action(c, "end")} data-testid={`campaign-end-${c.id}`} title="End now"
                    className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 text-neutral-400 hover:border-red-500/50 hover:text-red-400"><Square className="h-3 w-3" /></button>
                )}
                {c.status === "draft" && (
                  <button onClick={() => action(c, "delete")} data-testid={`campaign-delete-${c.id}`} title="Delete draft"
                    className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 text-neutral-400 hover:border-red-500/50 hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
                )}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
